<button type="button" class="jump-pill" [class.visible]="$showJumpPill()" [attr.aria-label]="$isStreaming() ? 'Streaming — jump to latest' : 'Jump to latest'" (click)="onJumpToBottom()">
  @if ($isStreaming()) {
    <span class="jump-dots"><span></span><span></span><span></span></span>
  } @else {
    <i class="fa-solid fa-arrow-down"></i>
  }
</button>

<div class="messages-area" #messagesContainer (scroll)="onScroll($event)" aria-live="polite">
  <div class="msg-col">
    @for (message of $messages(); track message.id; let idx = $index) {
      @if (message.role === 'user') {
        <div class="msg-user-anchor" [attr.data-message-id]="message.id">
          <agent-user-message [message]="message" />
        </div>
      } @else if (message.role === 'assistant') {
        @let enriched = $enrichedMessages().get(message.id);
        <!-- {} -->
        <div class="msg-ai-group">

          @for (item of enriched?.items ?? []; track item.trackKey) {
            @switch (item.kind) {
              @case ('text') {
                <agent-agent-message [message]="message" [revealedText]="item.renderedText" />
              }
              @case ('file') {
                <!-- File parts rendered at the bottom of the message group -->
              }
              @case ('bill-scan') {
                <div class="tool-cards">
                  <agent-bill-scan-card [toolInput]="item.toolInput" [conversationFiles]="$conversationFiles()" [blobUrls]="$blobUrls()" [initialStatus]="getCardActionStatus(item.toolCallId)" (scanComplete)="onBillScanComplete(item.toolCallId, $event)" (decline)="onBillScanDecline(item.toolCallId)" />
                </div>
              }
              @case ('special-tool') {
                <div class="tool-cards">
                  <!-- A pending write-tool approval card is NOT rendered inline here: the chat container's
                       composer overlay owns the single pending approval (only one card is ever pending),
                       exactly like the interview question card. Once resolved the part flips to
                       output-available/denied and stops matching here, so this case only renders the
                       interview recap below. -->
                  @if (item.isInterview && item.toolState === 'output-available') {
                    @if (item.badgeLines.length) {
                      <div class="interview-recap">
                        <button class="interview-recap-header" type="button" (click)="toggleInterviewRecap(item.toolCallId)" [attr.aria-expanded]="isInterviewExpanded(item.toolCallId)">
                          <i class="fa-solid fa-chevron-right interview-recap-chev" [class.open]="isInterviewExpanded(item.toolCallId)"></i>
                          <span>{{ item.badgeSummary }}</span>
                        </button>
                        @if (isInterviewExpanded(item.toolCallId)) {
                          <div class="interview-recap-body">
                            @for (line of item.badgeLines; track $index) {
                              <div class="interview-recap-line">
                                <span class="interview-recap-q">{{ line.question }}</span>
                                <span class="interview-recap-a" [class.skipped]="line.skipped">{{ line.answer }}</span>
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }
                  }
                </div>
              }
              @case ('activity-block') {
                <agent-activity-block [parts]="item.parts" [messageId]="message.id" [isCollapsed]="$collapsedActivityBlocks().has(item.blockKey)" [revealedTextParts]="$revealedTextParts()" [expandedTools]="$expandedTools()" (toggleCollapse)="activityBlockToggled.emit(item.blockKey)" (toolExpandToggled)="toolExpandToggled.emit($event)" />
              }
              @case ('download') {
                <agent-file-download-list [files]="item.files" />
              }
            }
          }

          <!-- File parts + tool output download cards — always at bottom of response -->
          @for (file of enriched?.fileItems ?? []; track file.partIndex) {
            <div class="file-chip">
              @if (file.isImage) {
                <p-image [src]="file.url" [preview]="true" [alt]="file.fileName" width="80" height="80" appendTo="body" />
              } @else {
                <span class="chip"><i class="chip-i fas {{ file.iconClass }}"></i>{{ file.fileName }}</span>
              }
            </div>
          }
          @if (enriched?.downloadFiles?.length) {
            <agent-file-download-list [files]="enriched.downloadFiles" />
          }

          <!-- Typing indicator: shown when last assistant message has no parts yet -->
          @if ($isStreaming() && message === $lastMessage() && message.parts.length === 0) {
            <span class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </span>
          }
        </div>
      }
    }

    <!-- Waiting for first assistant response after user message -->
    @if ($isStreaming() && $lastMessageIsUser()) {
      <div class="msg-ai-group">
        <span class="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </div>
    }

    <!-- Inline error alerts (thread-scoped) -->
    @if ($streamingError(); as error) {
      <div class="msg-ai-group">
        <w-alert severity="danger" displayStyle="standard" orientation="horizontal" [closable]="false" [detail]="friendlyErrorMessage(error)" [actions]="$retryActions()" />
      </div>
    } @else if ($threadLoadError(); as error) {
      <div class="msg-ai-group">
        <w-alert severity="danger" displayStyle="standard" orientation="horizontal" [closable]="false" [detail]="error" />
      </div>
    }

    <div class="scroll-spacer" [style.min-height.px]="$spacerHeight()"></div>
  </div>
</div>
